import React, { useEffect, useState, useCallback } from "react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { LayoutDashboard, Home, FileText, CalendarClock, Gift, Bell, User, LogOut, MessageCircle, Receipt } from "lucide-react";
import SEO from "@/components/SEO";
import { api } from "@/lib/api";
import { useSite, whatsappLink } from "@/context/SiteContext";

const NAV = [
  { to: "/account", label: "Home", icon: LayoutDashboard, end: true },
  { to: "/account/properties", label: "Properties", icon: Home },
  { to: "/account/quotes", label: "Quotes", icon: FileText },
  { to: "/account/bookings", label: "Bookings", icon: CalendarClock },
  { to: "/account/invoices", label: "Invoices", icon: Receipt },
  { to: "/account/rewards", label: "Rewards", icon: Gift },
  { to: "/account/profile", label: "Profile", icon: User },
];
// Mobile bottom nav keeps 5 primary destinations only.
const MOBILE_NAV = [NAV[0], NAV[1], NAV[3], NAV[4], NAV[6]];

export default function AccountLayout() {
  const nav = useNavigate();
  const loc = useLocation();
  const site = useSite();
  const [me, setMe] = useState(null);
  const [ready, setReady] = useState(false);
  const [unread, setUnread] = useState(0);

  const reloadMe = useCallback(async () => {
    try {
      const { data } = await api.get("/client/me");
      setMe(data);
      return data;
    } catch {
      nav(`/account/login?next=${encodeURIComponent(loc.pathname)}`);
      return null;
    }
  }, [nav, loc.pathname]);

  const reloadUnread = useCallback(async () => {
    try { const { data } = await api.get("/client/notifications"); setUnread(data.unread || 0); } catch { /* noop */ }
  }, []);

  useEffect(() => {
    (async () => { const m = await reloadMe(); if (m) await reloadUnread(); setReady(true); })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logout = async () => {
    try { await api.post("/client/logout"); } catch (e) { console.error("client logout failed", e); }
    window.dispatchEvent(new Event("ascendia-auth-change"));
    nav("/account/login");
  };

  if (!ready) {
    return <div className="min-h-screen bg-[#0B0B0C] flex items-center justify-center text-muted-c text-sm">Loading your Ascendia…</div>;
  }

  const firstName = (me?.first_name || "there").split(" ")[0];

  return (
    <div className="min-h-screen bg-[#0B0B0C] text-white">
      <SEO title="My Ascendia" noindex />
      {/* Top bar */}
      <header className="sticky top-0 z-30 border-b border-[color:var(--border-subtle)] bg-[#0B0B0C]/95 backdrop-blur">
        <div className="mx-auto w-full max-w-[1180px] px-4 sm:px-6 flex h-16 items-center justify-between gap-3">
          <button onClick={() => nav("/account")} className="flex items-center gap-2.5" data-testid="account-logo">
            <img src={site.logo} alt="Ascendia Detail" className="h-8 w-8 object-contain" />
            <span className="font-display font-semibold text-white">My <span className="text-gold">Ascendia</span></span>
          </button>
          <div className="flex items-center gap-1.5">
            <NavLink to="/account/notifications" data-testid="account-notifications-bell" className="relative inline-flex h-10 w-10 items-center justify-center rounded-lg border border-[color:var(--border-subtle)] text-white/80 hover:bg-white/5">
              <Bell className="h-4 w-4" />
              {unread > 0 && <span className="absolute -top-1 -right-1 h-4 min-w-[16px] px-1 rounded-full bg-gold text-[10px] font-semibold text-black flex items-center justify-center">{unread}</span>}
            </NavLink>
            <button onClick={logout} data-testid="account-logout" className="inline-flex items-center gap-2 rounded-lg border border-[color:var(--border-subtle)] px-3 h-10 text-sm text-white/80 hover:bg-white/5"><LogOut className="h-4 w-4" /> <span className="hidden sm:inline">Sign out</span></button>
          </div>
        </div>
      </header>

      <div className="mx-auto w-full max-w-[1180px] px-4 sm:px-6 py-8 grid lg:grid-cols-[220px_1fr] gap-8">
        {/* Desktop side nav */}
        <aside className="hidden lg:block">
          <p className="kicker mb-4">Welcome back, {firstName}</p>
          <nav className="space-y-1">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.end} data-testid={`account-nav-${n.label.toLowerCase()}`}
                className={({ isActive }) => `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors ${isActive ? "bg-gold/10 text-gold-soft border border-[color:var(--border-subtle)]" : "text-white/75 hover:bg-white/5"}`}>
                <n.icon className="h-4 w-4" /> {n.label}
              </NavLink>
            ))}
            <NavLink to="/account/notifications" className={({ isActive }) => `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors ${isActive ? "bg-gold/10 text-gold-soft border border-[color:var(--border-subtle)]" : "text-white/75 hover:bg-white/5"}`}>
              <Bell className="h-4 w-4" /> Notifications {unread > 0 && <span className="ml-auto h-5 min-w-[20px] px-1 rounded-full bg-gold text-[11px] font-semibold text-black flex items-center justify-center">{unread}</span>}
            </NavLink>
          </nav>
          <div className="gold-hairline my-5" />
          <a href={whatsappLink(site)} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 rounded-lg px-3 py-2.5 text-sm text-white/75 hover:bg-white/5"><MessageCircle className="h-4 w-4 text-[#25D366]" /> Contact Ascendia</a>
        </aside>

        {/* Main content */}
        <main className="pb-24 lg:pb-0">
          <Outlet context={{ me, reloadMe, reloadUnread, logout, unread }} />
        </main>
      </div>

      {/* Mobile bottom nav */}
      <nav className="lg:hidden fixed bottom-0 inset-x-0 z-40 border-t border-[color:var(--border-subtle)] bg-[#0B0B0C]/95 backdrop-blur" style={{ paddingBottom: "env(safe-area-inset-bottom)" }} data-testid="account-bottom-nav">
        <div className="grid grid-cols-5">
          {MOBILE_NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} data-testid={`account-tab-${n.label.toLowerCase()}`}
              className={({ isActive }) => `flex flex-col items-center justify-center gap-1 py-2.5 text-[10px] ${isActive ? "text-gold-soft" : "text-white/60"}`}>
              <n.icon className="h-5 w-5" /> {n.label}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}
